/**
 * Calendar maths for the date range picker, on account-local 'YYYY-MM-DD' strings: whole days and
 * months are added in UTC, never in the browser's time zone (packages/shared/src/dates.ts).
 */
import { addDays } from "@brecx/shared";

/** "2026-10" of a date. */
export const monthOf = (date: string) => date.slice(0, 7);

/** A month `n` months away: ("2026-10", 3) → "2027-01". */
export function addMonths(month: string, n: number): string {
  const [y = 0, m = 1] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

/** The six weeks a month's page shows, Sunday first, with the days of its neighbours around it. */
export function monthWeeks(month: string): string[][] {
  const first = `${month}-01`;
  const start = addDays(first, -new Date(`${first}T00:00:00Z`).getUTCDay());
  return Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d)));
}

/** "October 2026" */
export const monthLabel = (month: string) =>
  new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

const short = (date: string, year: boolean) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(year ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });

/** "Oct 13, 2026" · "Sep 13 – Oct 12, 2026" · "Dec 20, 2025 – Jan 5, 2026" */
export function rangeLabel(from: string, to: string): string {
  if (from === to) return short(to, true);
  return `${short(from, from.slice(0, 4) !== to.slice(0, 4))} – ${short(to, true)}`;
}

export interface Preset {
  label: string;
  /** one of the app's own "last N complete days" ranges … */
  days?: number;
  /** … or fixed dates */
  from?: string;
  to?: string;
}

/**
 * The ready-made ranges. "Last N days" are the app's own ranges (complete days, ending yesterday);
 * the rest are fixed dates counted from `today` in the account's time zone.
 */
export function presets(today: string): Preset[] {
  const yesterday = addDays(today, -1);
  const sunday = addDays(today, -new Date(`${today}T00:00:00Z`).getUTCDay());
  const month = monthOf(today);
  const lastMonth = addMonths(month, -1);
  return [
    { label: "Today", from: today, to: today },
    { label: "Yesterday", from: yesterday, to: yesterday },
    { label: "Last 7 days", days: 7 },
    { label: "Last 14 days", days: 14 },
    { label: "This week", from: sunday, to: today },
    { label: "Last week", from: addDays(sunday, -7), to: addDays(sunday, -1) },
    { label: "Last 30 days", days: 30 },
    { label: "Last 60 days", days: 60 },
    { label: "Last 90 days", days: 90 },
    { label: "This month", from: `${month}-01`, to: today },
    { label: "Last month", from: `${lastMonth}-01`, to: addDays(`${month}-01`, -1) },
    { label: "Last 6 months", from: `${addMonths(month, -6)}-01`, to: addDays(`${month}-01`, -1) },
    { label: "Year to date", from: `${today.slice(0, 4)}-01-01`, to: today },
  ];
}
